import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Spin, Alert, Pagination, Empty, message } from 'antd';
import { FrameWithLabelAndPredictionQuery } from '../../redux/slices/predictions';
import { useCreateReviewMutation } from '../../redux/slices/reviews';

import { SegmentationPredictionToCompare } from 'models/predictions';
import { Decision, Review } from 'models/reviews';
import { PaginationWrapper, PageWrapper } from 'components/ui/wrappers';
import { ReviewChoice } from './components/ReviewControls';
import PredictionFilters from 'components/Predictions/components/predictionFilters';
import ViewOptions from './components/ViewOptions';
import PredictionCard from './components/PredictionCard';
import { usePredictionFilterState } from 'hooks/useFilterState';

import { PredictionsGrid, FilterWrapper } from './styled';
import { config } from 'services/config';

type ReviewableObject = {
  uuid: string;
  icid: string;
};

const Predictions: React.FC = () => {
  const { filterState } = usePredictionFilterState();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(100);
  const [shouldFetchPredictions, setShouldFetchPredictions] = useState(false);
  // Local flag to indicate user has applied filters and we're waiting for data
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  // Initialize blinded state from URL params
  const [isBlinded, setIsBlinded] = useState(() => {
    return searchParams.get('blinded') === 'true';
  });

  const queryParams = useMemo(
    () => ({
      page,
      size: size,
      is_reviewed: filterState.is_reviewed,
      review_decision: filterState.review_decision,
      has_corresponding_label: filterState.has_corresponding_label,
      features: filterState.features,
      datasource: filterState.datasource,
      dicom_icid: filterState.dicom_icid,
      segmentation_prediction_icid: (filterState as any).segmentation_prediction_icid,
      segmentation_label_icid: (filterState as any).segmentation_label_icid,
      // dicom_fid: (filterState as any).dicom_fid,
      study_icid: filterState.study_icid,
      study_fid: (filterState as any).study_fid,
      source_uuid: (filterState as any).source_uuid,
      created_at_after: filterState.created_at_after,
      created_at_before: filterState.created_at_before,
      label_created_at_after: filterState.label_created_at_after,
      label_created_at_before: filterState.label_created_at_before,
      blinded: isBlinded,
    }),
    [page, size, filterState, isBlinded]
  );

  const { data, isFetching, error } = FrameWithLabelAndPredictionQuery(queryParams, {
    skip: !shouldFetchPredictions,
    refetchOnMountOrArgChange: true,
  });

  // Extract a friendly backend error message when available so we can show it inline.
  const backendErrorMessage = useMemo(() => {
    if (!error) return null;
    const errAny: any = error;
    // RTK Query usually exposes `data` for server responses
    if (errAny?.data) {
      const d = errAny.data;
      if (typeof d === 'string') return d;
      if (d?.detail) return d.detail;
      if (d?.sourceUuid) return d.sourceUuid;
      if (d?.source_uuid)
        return Array.isArray(d.source_uuid) ? d.source_uuid.join('; ') : d.source_uuid;
      // Flatten typical DRF ValidationError shapes like { field: ["msg"] }
      if (typeof d === 'object') {
        const parts: string[] = [];
        for (const k of Object.keys(d)) {
          const v = (d as any)[k];
          if (Array.isArray(v)) parts.push(`${k}: ${v.join('; ')}`);
          else parts.push(`${k}: ${String(v)}`);
        }
        return parts.join(' | ');
      }
    }
    // Fallbacks
    if (errAny?.error) return String(errAny.error);
    return null;
  }, [error]);

  // Reset pagination when blinded mode changes.
  // NOTE: intentionally do NOT depend on `shouldFetchPredictions` here to avoid
  // resetting the page when we auto-enable fetching from a URL `?page=` param.
  useEffect(() => {
    if (shouldFetchPredictions) {
      setPage(1);
    }
    // only run when isBlinded changes
  }, [isBlinded]);

  // Sync initial page from URL (?page=...); default to 1 if invalid/missing
  useEffect(() => {
    const qp = searchParams.get('page');
    const next = qp ? parseInt(qp, 10) : 1;
    if (Number.isFinite(next) && next > 0 && next !== page) {
      setPage(next);
    }
  }, [searchParams]);

  // If ?page is present in URL, auto-enable fetching predictions
  useEffect(() => {
    const hasPage = !!searchParams.get('page');
    if (hasPage && !shouldFetchPredictions) {
      setShouldFetchPredictions(true);
    }
  }, [searchParams, shouldFetchPredictions]);

  // Helper to set page in URL (always include ?page=...)
  const writePageToURL = (next: number, nextSize?: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', String(next));
    const sizeToWrite = typeof nextSize === 'number' ? nextSize : size;
    params.set('items_per_page', String(sizeToWrite));
    setSearchParams(params, { replace: true });
  };

  // Sync initial size from URL (?items_per_page=...); default to current `size` if invalid/missing
  useEffect(() => {
    const qp = searchParams.get('items_per_page');
    const next = qp ? parseInt(qp, 10) : undefined;
    if (next && Number.isFinite(next) && next > 0 && next !== size) {
      setSize(next);
    }
  }, [searchParams]);

  // Create review mutation
  const [createReview] = useCreateReviewMutation();

  // Handler for when filters are applied by the filter component
  const handleFiltersApplied = () => {
    // mark that user initiated an apply and show loading until data arrives
    setIsApplyingFilters(true);
    setPage(1);
    setShouldFetchPredictions(true);
    // persist page to URL so other components and direct links behave the same
    writePageToURL(1);
  };

  // Handler for when filters are cleared by the filter component
  const handleFiltersClear = () => {
    setShouldFetchPredictions(false);
    // if user cleared filters, cancel the applying state
    setIsApplyingFilters(false);
  };

  // When the query finishes loading, clear the applying flag so UI updates
  useEffect(() => {
    // Clear applying flag once the background fetch completes
    if (isApplyingFilters && !isFetching) {
      setIsApplyingFilters(false);
    }
  }, [isApplyingFilters, isFetching]);

  const onPaginationChange = (newPage: number, pageSize: number) => {
    setPage(newPage);
    setSize(pageSize);
    writePageToURL(newPage, pageSize);
  };

  const onShowSizeChange = (_current: number, newSize: number) => {
    setSize(newSize);
    setPage(1);
    writePageToURL(1, newSize);
  };

  const handleReview = async (
    left: ReviewableObject,
    right: ReviewableObject,
    choice: ReviewChoice,
    comment?: string
  ) => {
    try {
      if (!left?.uuid || !right?.uuid) {
        throw new Error('Missing left/right uuid for review');
      }

      let leftSideReview: Review = {
        object_uuid: left.uuid,
        object_icid: left.icid,
        object_type: 'AI_COMPARISON',
        decision: Decision.ACCEPTED,
        comment: comment || '',
        annotation_link: '',
        related_object_uuid: right.uuid,
      };
      let rightSideReview: Review = {
        object_uuid: right.uuid,
        object_icid: right.icid,
        object_type: 'AI_COMPARISON',
        decision: Decision.ACCEPTED,
        comment: comment || '',
        annotation_link: '',
        related_object_uuid: left.uuid,
      };

      if (choice === ReviewChoice.ACCEPT_LEFT) {
        leftSideReview = {
          ...leftSideReview,
          decision: Decision.ACCEPTED,
        };

        rightSideReview = {
          ...rightSideReview,
          decision: Decision.REJECTED,
        };
      } else if (choice === ReviewChoice.ACCEPT_RIGHT) {
        leftSideReview = {
          ...leftSideReview,
          decision: Decision.REJECTED,
        };

        rightSideReview = {
          ...rightSideReview,
          decision: Decision.ACCEPTED,
        };
      } else if (choice === ReviewChoice.ACCEPT_NEITHER) {
        leftSideReview = {
          ...leftSideReview,
          decision: Decision.REJECTED,
        };

        rightSideReview = {
          ...rightSideReview,
          decision: Decision.REJECTED,
        };
      } else if (choice === ReviewChoice.ACCEPT_BOTH) {
        leftSideReview = {
          ...leftSideReview,
          decision: Decision.ACCEPTED,
        };

        rightSideReview = {
          ...rightSideReview,
          decision: Decision.ACCEPTED,
        };
      } else {
        throw new Error(`Invalid review choice: ${choice}`);
      }

      await createReview(leftSideReview).unwrap();
      await createReview(rightSideReview).unwrap();

      message.success(`Review updated: ${choice}`);
      return Promise.resolve();
    } catch (error) {
      message.error('Failed to submit review');
      return Promise.reject(error);
    }
  };

  // Transform API data to match component structure
  const segmentationPredictions = useMemo(() => {
    if (!data?.results) return [];

    // Backend may return two shapes:
    // 1) Legacy: results is already an array of SegmentationPredictionToCompare
    // 2) New: results is an array of frames where each frame has `frame` and `segmentations: []`
    const first = data.results[0];
    if (first && (first as any).segmentations && (first as any).frame) {
      // Frame-centric response: map each frame into one SegmentationPredictionToCompare-like object
      const out: SegmentationPredictionToCompare[] = [];
      (data.results as any[]).forEach((frameGroup: any) => {
        const frame = frameGroup.frame;
        const study = frameGroup.study || { uuid: '', icid: frame?.icid || '' };
        const dicom = frameGroup.dicom || { uuid: frame?.uuid || '', icid: frame?.icid || '' };
        const segs = Array.isArray(frameGroup.segmentations) ? frameGroup.segmentations : [];

        out.push({
          segmentationClass: segs[0]?.segmentationClass || '',
          frame: {
            uuid: frame?.uuid || '',
            icid: frame?.icid || '',
            fileUrl: frame?.fileUrl || '',
          },
          dicom,
          study,
          // Keep legacy single fields empty; provide full `segmentations` on the object for transform logic
          label: null as any,
          prediction: null as any,
          segmentationA: null,
          segmentationB: null,
          isApproved: false,
          isRejected: false,
          isReviewed: false,
          isAcceptAi: false,
          isAcceptHuman: false,
          isAcceptBoth: false,
          isAcceptNeither: false,
          // attach original segmentations array for downstream transform
          ...({ segmentations: segs } as any),
        } as SegmentationPredictionToCompare);
      });
      return out;
    }

    return data.results as SegmentationPredictionToCompare[];
  }, [data]);

  const paginatedResults = useMemo(
    () => ({
      items: segmentationPredictions,
      total: data?.count || 0,
      page,
      size,
    }),
    [segmentationPredictions, data, page, size]
  );

  const hasAnyFilter = (filterState: any) => {
    return (
      filterState.is_reviewed !== undefined ||
      filterState.review_decision ||
      filterState.has_corresponding_label !== undefined
    );
  };

  const hasActiveFilters = useMemo(() => hasAnyFilter(filterState), [filterState]);

  const handleDicomClick = (dicomIcid: string): void => {
    const token = localStorage.getItem('AccessToken');
    const dicomBaseUrl = config.ATLAS_URL;
    if (dicomIcid) {
      const url = `${dicomBaseUrl}/?dicom=${encodeURIComponent(dicomIcid)}${
        token ? `&t=${encodeURIComponent(token)}` : ''
      }`;
      window.open(url, '_blank');
    }
  };

  // do not short-circuit rendering on error — show inline friendly message below ViewOptions

  return (
    <PageWrapper>
      <FilterWrapper>
        <PredictionFilters
          onFiltersApplied={handleFiltersApplied}
          onFiltersClear={handleFiltersClear}
        />
      </FilterWrapper>

      <ViewOptions isBlinded={isBlinded} onBlindedChange={setIsBlinded} />

      {!shouldFetchPredictions && !hasActiveFilters ? (
        <Empty description="Please apply filters to view predictions" />
      ) : isFetching || isApplyingFilters ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          message={backendErrorMessage ? 'Filter error' : 'Error'}
          description={backendErrorMessage || 'Failed to load Side-by-Side Segmentations'}
          type="error"
          showIcon
          style={{ margin: '20px' }}
        />
      ) : paginatedResults.items.length === 0 ? (
        <Empty description="No predictions available" />
      ) : (
        <>
          <PredictionsGrid>
            {paginatedResults.items.map(segmentationPrediction => {
              // Generate stable unique key by combining both UUIDs
              const keyA =
                segmentationPrediction.segmentationA?.uuid ||
                segmentationPrediction.label?.uuid ||
                '';
              const keyB =
                segmentationPrediction.segmentationB?.uuid ||
                segmentationPrediction.prediction?.uuid ||
                '';
              const uniqueKey =
                keyA || keyB
                  ? `${keyA}-${keyB}`
                  : segmentationPrediction.frame?.icid || `${Math.random()}`;

              return (
                <PredictionCard
                  key={uniqueKey}
                  segmentationPrediction={segmentationPrediction}
                  isBlinded={isBlinded}
                  onReview={handleReview}
                  onDicomClick={handleDicomClick}
                />
              );
            })}
          </PredictionsGrid>
          <PaginationWrapper>
            <Pagination
              total={paginatedResults.total}
              defaultPageSize={size}
              current={page}
              onShowSizeChange={onShowSizeChange}
              onChange={onPaginationChange}
              showTotal={totalItems => `Total ${totalItems} items`}
            />
          </PaginationWrapper>
        </>
      )}
    </PageWrapper>
  );
};

export default Predictions;
